import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import { useNotifications } from '@decisiv/ui-components';
import noop from 'lodash/noop';
import { AUTH_ERROR_EVENT } from '../../api/api';

// Parallel requests can each fail with the same status; collapse a burst into a
// single toast within this window so the user isn't stacked with duplicates.
const DEDUPE_MS = 3000;

/**
 * Bridges auth failures raised by the axios interceptors (api.js) to the React
 * tree: shows an error toast and, for a 401, redirects to /sign_in with
 * client-side navigation so the toast survives the route change. Renders
 * nothing.
 */
export default function AuthErrorHandler() {
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const lastShownAtRef = useRef({});

  useEffect(() => {
    const handleAuthError = (event) => {
      const status = event.detail?.status;
      const now = Date.now();

      if (now - (lastShownAtRef.current[status] || 0) < DEDUPE_MS) return;
      lastShownAtRef.current[status] = now;

      if (status === 401) {
        notify(
          {
            intent: 'warning',
            title: t`Session expired`,
            duration: 5000,
            onClose: noop,
          },
          t`Please sign in again.`,
        );
        navigate('/sign_in');
      } else if (status === 403) {
        notify(
          {
            intent: 'danger',
            title: t`Access denied`,
            duration: 5000,
            onClose: noop,
          },
          t`You don't have permission to perform this action.`,
        );
      }
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
  }, [notify]);

  return null;
}
